import { Platform } from 'react-native';

import { apiService } from '@/services/api-service';
import { openFetchedFile } from '@/utils/open-fetched-pdf';
import { safeTabBlobUrl, safeTabType } from '@/utils/safe-blob-types';

/**
 * Fetching stored documents for preview and download (issue #373).
 *
 * ## Why this exists at all
 *
 * The proxy endpoint requires our `Authorization: Device <token>` header, and
 * `<iframe src>`, `<img src>` and `<a href>` CANNOT send custom headers. So a document
 * URL can never be handed to the browser directly — the bytes have to be fetched in JS
 * (where the header can be attached) and then handed to the viewer as a blob URL.
 *
 * The same two-step already exists elsewhere in the app: R2 attachments do it in
 * `components/attachments/use-attachment-image.ts`, and the SQL admin's CSV export does
 * the blob→`a.download` half. This module is the document equivalent, in one place.
 *
 * Deliberately provider-agnostic: it knows only that `/documents/…` returns bytes behind
 * our auth header. Nothing here names a storage service.
 */

/** Extensions the browser renders natively, so preview is worth offering. */
const PREVIEWABLE = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'txt']);

export const canPreview = (extension: string): boolean => PREVIEWABLE.has(extension.toLowerCase());

export const isImage = (extension: string): boolean =>
	['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension.toLowerCase());

/**
 * Types that must open in their own TAB rather than an in-page frame.
 *
 * Chrome refuses to run its PDF viewer inside a sandboxed iframe — the frame renders
 * "This page has been blocked by Chrome" — and un-sandboxing an iframe to get a PDF to
 * display is the wrong trade. A real tab also gets the native viewer's find-in-document,
 * page navigation and print, which is what makes a scanned document actually usable.
 */
export const opensInNewTab = (extension: string): boolean =>
	['pdf', 'txt'].includes(extension.toLowerCase());

/**
 * Fetch a document and return an object URL for it.
 *
 * The caller MUST revoke the returned url when done (`URL.revokeObjectURL`) or the blob
 * stays in memory for the life of the page — a few scanned PDFs is tens of MB.
 */
export async function fetchDocumentObjectUrl(path: string): Promise<string> {
	return (await fetchDocumentBlobUrl(path)).url;
}

/**
 * `fetchDocumentObjectUrl`, plus the type the server sent — for a caller that picks its
 * renderer by type rather than by a file extension it already knows. Same ownership rule:
 * the caller MUST revoke `url`.
 */
export async function fetchDocumentBlobUrl(path: string): Promise<{ url: string; mimeType: string }> {
	const response = await apiService.downloadFile(path, { timeoutMs: 120_000 });
	if (!response.ok) {
		throw new Error(await documentErrorMessage(response));
	}
	const blob = await response.blob();
	const mimeType = (blob.type || response.headers?.get('content-type') || '').split(';')[0].trim().toLowerCase();
	return { url: URL.createObjectURL(blob), mimeType };
}

/**
 * Fetch a document as TEXT.
 *
 * Deliberately not routed through `fetchDocumentObjectUrl`: a text preview never needs an
 * object URL, and creating one would hand the caller a native resource to revoke for no
 * reason. Lives here rather than in the caller so the auth header and the error-message
 * handling stay in one place.
 *
 * `maxChars` is a RENDER cap, separate from the byte ceiling the caller already applied. A
 * 900 KB log is under the byte ceiling and still not something to lay out in a 300px column.
 */
export async function fetchDocumentText(path: string, maxChars: number): Promise<{ text: string; truncated: boolean }> {
	const response = await apiService.downloadFile(path, { timeoutMs: 120_000 });
	if (!response.ok) {
		throw new Error(await documentErrorMessage(response));
	}
	const text = await response.text();
	return text.length > maxChars
		? { text: text.slice(0, maxChars), truncated: true }
		: { text, truncated: false };
}

/**
 * How long a blob URL handed to another tab stays alive before being revoked.
 *
 * It can't be revoked immediately — the new tab hasn't fetched it yet and would show a
 * blank viewer. Nor can it leak forever; a few scanned PDFs is tens of MB.
 */
const NEW_TAB_BLOB_TTL_MS = 60_000;

/** RN defines a global `window` with no `open`, so `typeof window` is not a web check. */
const isWebWindow = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

/** Replace a tab's body with one line of plain text. */
function writeTabMessage(tab: Window, message: string, color: string): void {
	const paragraph = tab.document.createElement('p');
	paragraph.style.font = '14px system-ui';
	paragraph.style.padding = '16px';
	paragraph.style.color = color;
	// textContent, never innerHTML: `message` can carry a provider-supplied filename, which
	// would otherwise be an HTML injection into a page we control.
	paragraph.textContent = message;
	tab.document.body.replaceChildren(paragraph);
}

/**
 * Open a tab NOW, before any await, and hand it back to be pointed somewhere later.
 *
 * Same trick as `openDocumentInNewTab` below, exposed separately for destinations that are
 * NOT our API: `window.open` after an `await` has lost the user-gesture context and the
 * popup blocker eats it, so the tab must be claimed synchronously on the click.
 */
export function openPendingTab(message = 'Opening…'): Window | null {
	const tab = isWebWindow() ? window.open('', '_blank') : null;
	if (tab) writeTabMessage(tab, message, '#333');
	return tab;
}

/**
 * Send an already-open tab to an EXTERNAL url.
 *
 * Deliberately a plain navigation, NOT `apiService.downloadFile`. That helper prefixes the
 * API base URL and attaches our auth header, so handing it a dropbox.com link made the
 * browser fetch `localhost:8887/https://www.dropbox.com/…` and render "Not Found" inside the
 * blank tab — a third-party URL is not ours to fetch, only to navigate to.
 */
export function navigatePendingTab(tab: Window | null, url: string): boolean {
	if (!tab) return false;
	tab.location.replace(url);
	return true;
}

/** Report a failure inside a tab already opened for it, rather than leaving it blank. */
export function failPendingTab(tab: Window | null, message: string): void {
	if (tab) writeTabMessage(tab, message, '#c62828');
}

/**
 * Open a document in a new tab, rendered by the browser's own viewer.
 *
 * The window is opened SYNCHRONOUSLY, before the fetch, and its location is set once the
 * bytes arrive. That ordering is the whole trick: `window.open` after an `await` has lost
 * the user-gesture context and Chrome's popup blocker eats it. Opening first and
 * navigating later keeps the gesture.
 *
 * Returns false when the popup was blocked anyway, so the caller can fall back to a
 * download rather than appearing to do nothing — and ALSO when `fileName` is not a type that
 * may be rendered (`safeTabType`): the blob would run in our origin, so the caller downloads it.
 */
export async function openDocumentInNewTab(path: string, fileName: string): Promise<boolean> {
	// Decided BEFORE opening a tab, from the name alone, so a refused type never flashes one.
	if (!safeTabType(fileName)) return false;
	const tab = isWebWindow() ? window.open('', '_blank') : null;
	if (!tab) return false;

	// Something to look at while the bytes are in flight — an unstyled blank tab reads
	// as a broken link.
	writeTabMessage(tab, 'Opening document…', '#333');

	try {
		const response = await apiService.downloadFile(path, { timeoutMs: 120_000 });
		if (!response.ok) throw new Error(await documentErrorMessage(response));

		// Re-typed from the extension, never the stored `mime_type` (see safe-blob-types.ts).
		const url = await safeTabBlobUrl(response, fileName);
		if (!url) throw new Error('This file type cannot be previewed');
		tab.location.replace(url);
		setTimeout(() => URL.revokeObjectURL(url), NEW_TAB_BLOB_TTL_MS);
		return true;
	} catch (e) {
		// Report inside the tab we already opened, instead of leaving it stuck on "Opening".
		writeTabMessage(tab, e instanceof Error ? e.message : String(e), '#c62828');
		throw e;
	}
}

/**
 * Trigger a browser download.
 *
 * Goes through the same authenticated fetch, then clicks a synthetic anchor at a blob
 * URL. `<a href="/documents/…">` would 401 — no auth header — so the anchor has to point at
 * bytes we already hold.
 */
export async function downloadDocument(path: string, filename: string): Promise<void> {
	if (!isWebWindow()) {
		// iOS: cache file + share sheet (Quick Look, Save to Files, AirDrop). The type is
		// inferred from the extension there, so a generic one is correct.
		await openFetchedFile(
			() => apiService.downloadFile(path, { timeoutMs: 120_000 }),
			filename,
			'application/octet-stream',
		);
		return;
	}
	const response = await apiService.downloadFile(path, { timeoutMs: 120_000 });
	if (!response.ok) {
		throw new Error(await documentErrorMessage(response));
	}
	const url = URL.createObjectURL(await response.blob());
	try {
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		anchor.click();
	} finally {
		// Safe to revoke immediately — the click has already started the download.
		URL.revokeObjectURL(url);
	}
}

/**
 * Turn a failed proxy response into something readable.
 *
 * The API answers with `{ type: 'error', message }`, and the message is deliberately
 * actionable (e.g. "File not found" for a link whose file has since moved out of view), so
 * prefer it over the bare status.
 */
async function documentErrorMessage(response: Response): Promise<string> {
	try {
		const body = await response.json() as { message?: string };
		if (body?.message) return body.message;
	} catch {
		// Non-JSON body — fall through to the status.
	}
	return `Could not load document (${response.status})`;
}

/** "1.4 MB". Bytes are what the provider reports; undefined renders as an em dash upstream. */
export function formatDocumentSize(bytes?: number): string {
	if (bytes == null) return '';
	if (bytes === 0) return '0 bytes';
	const units = ['bytes', 'KB', 'MB', 'GB'];
	const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	const value = bytes / Math.pow(1024, power);
	// Whole numbers for bytes, one decimal above that — "1.4 MB" not "1.44 MB".
	return `${power === 0 ? value : Number(value.toFixed(1))} ${units[power]}`;
}
